import { camelCase } from "lodash-es";
import {
  type DiJwtLimitedScope,
  type DiJwtPermission,
  type DiJwtTokenPayload,
  extractDiJwtPayload,
  type NumericDate,
} from "~/api/auth/jwt";
import {
  type AccessToken,
  type ImpersonatorClaim,
} from "~/api/generated/model";
import {
  FEATURE_FLAG_FOREIGN_KEYS,
  type FeatureFlagForeignKey,
} from "~/feature-flags-defaults";
import { type SnakeCaseToCamelCase } from "~/utils/types";

export type AuthUserRequiredAction = {
  name: DiJwtLimitedScope;
  cooldownUntil: Date | null;
};

type CamelCaseDiJwtLimitedScope = SnakeCaseToCamelCase<DiJwtLimitedScope>;

export type AuthUserRequiredActionsMap = Partial<
  Record<CamelCaseDiJwtLimitedScope, AuthUserRequiredAction>
>;

export type AuthUser = {
  get id(): string;
  get sessionId(): string | null;
  get email(): string;
  get firstName(): string;
  get lastName(): string;
  get fullName(): string;
  get phoneNumber(): string | null;

  get isAuthenticated(): boolean;

  get impersonator(): ImpersonatorClaim | null;

  get isImpersonated(): boolean;

  get requiredActions(): AuthUserRequiredActionsMap | null;

  get permissions(): DiJwtPermission[];

  /**
   * feature flags comes as string[], there's no insurance that entries will be of the type:
   * FeatureFlagForeignKey, but is defined as such to give clarity to what is the expected content.
   */
  get featureFlags(): FeatureFlagForeignKey[];

  get token(): {
    issuedAt: Date;
    expireAt: Date;
  };
} | null;

export const jwtTokenToAuthUser = (jwt: string | null): AuthUser => {
  const jwtPayload = extractDiJwtPayload(jwt);
  return jwtPayloadToAuthUser(jwtPayload);
};

export const jwtPayloadToAuthUser = (
  jwtPayload: DiJwtTokenPayload | null,
): AuthUser => {
  if (!jwtPayload) return null;

  const requiredActions = jwtPayload.required_actions.length
    ? jwtPayload.required_actions.reduce<AuthUserRequiredActionsMap>(
        (map, { name, cooldown }) => {
          map[camelCase(name) as CamelCaseDiJwtLimitedScope] = {
            name,
            cooldownUntil:
              typeof cooldown === "number" ? numericDateToDate(cooldown) : null,
          };
          return map;
        },
        {},
      )
    : null;

  const isAuthenticated = requiredActions === null;

  const permissions = jwtPayload.permissions ?? [];

  const impersonator = jwtPayload.impersonator ?? null;

  return {
    id: jwtPayload.user_id,
    sessionId: jwtPayload.sid ?? null,
    email: jwtPayload.sub,

    firstName: jwtPayload.firstName,
    lastName: jwtPayload.lastName,
    fullName: jwtPayload.firstName + " " + jwtPayload.lastName,
    phoneNumber: jwtPayload.phoneNumber ?? null,

    isAuthenticated,
    impersonator,
    isImpersonated: impersonator !== null,
    requiredActions,
    permissions,

    featureFlags: parseJwtFeatureFlags(jwtPayload),

    token: {
      issuedAt: numericDateToDate(jwtPayload.iat),
      expireAt: numericDateToDate(jwtPayload.exp),
    },
  };
};

const numericDateToDate = (numericDate: NumericDate): Date => {
  const epochSecs = Math.ceil(numericDate);
  const epochMs = epochSecs * 1000;
  return new Date(epochMs);
};

const parseJwtFeatureFlags = (
  jwtPayload: AccessToken,
): FeatureFlagForeignKey[] => {
  if (!jwtPayload.featureFlags?.length) return [];

  const flags = jwtPayload.featureFlags.split(",").map((f) => f.trim());

  return flags.filter((f) =>
    (FEATURE_FLAG_FOREIGN_KEYS as string[]).includes(f),
  ) as FeatureFlagForeignKey[];
};
